import {Component, inject, Injector, OnDestroy, OnInit, signal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {finalize, Subscription} from 'rxjs';

import {TableComponent} from '@shared/components/table/table.component';
import {TableConfig} from '@shared/interfaces/table/table-config';
import {ActivatedRoute, ParamMap} from '@angular/router';
import {ConnectionPointDto, ConnectionPointHistoricalDto, ConnectionPointService} from '@app/api/nts';
import {connectionpointHistoryTable} from '@shared/table-configs/connectionpoint/connectionpoint-history.table';
import {TranslatePipe} from '@ngx-translate/core';
import {statePartial} from '@shared/partials/state.partial';
import {TableCellComponent} from '@shared/components/table/table-cell/table-cell.component';
import {FlattenedConnectionPointHistoricalDto} from '@shared/interfaces/dto/FlattenedConnectionPointHistoricalDto';
import {systemServicesDetailsMasterdata} from '@shared/interfaces/nts/system-services-details.masterdata';
import {TranslateCellComponent} from '@shared/components/table/cells/translate-cell/translate-cell.component';
import {CardComponent} from '@shared/components/card/card.component';
import {ConnectionRight} from '@shared/interfaces/nts/connection-right';
import {connectionRightHistoryTable} from '@shared/table-configs/connection-right/connection-right-history.table';
import {StateManagerService} from '@shared/state/state-manager.service';

@Component({
  selector: 'app-connectionpoint-history',
  imports: [
    TableComponent,
    CardComponent,
    TranslatePipe,
    TableCellComponent
],
  templateUrl: './connectionpoint-history.component.html',
  styleUrl: './connectionpoint-history.component.css'
})
export class ConnectionpointHistoryComponent implements OnInit, OnDestroy {
  injector = inject(Injector);
  public tableData: FlattenedConnectionPointHistoricalDto[] = [];
  public id!: string;
  public selectedElements: FlattenedConnectionPointHistoricalDto[] = [];
  public connectionPoint?: ConnectionPointDto;
  public connectionRights: ConnectionRight[] = [];
  public readonly historyState = signal<'loading' | 'ready' | 'error'>('loading');
  public readonly rightsState = signal<'loading' | 'ready' | 'error'>('loading');
  public readonly connectionPointState = signal<'loading' | 'ready' | 'error'>('loading');
  private readonly subscriptions = new Subscription();
  private routeRequests = new Subscription();
  private historyRequest?: Subscription;
  private rightsRequest?: Subscription;
  public readonly connectionpointHistoryTableConfig: TableConfig<ConnectionPointHistoricalDto> = {
    ...connectionpointHistoryTable,
    refreshable: true,
    refresh: () => this.fetchHistory(),
  };
  public readonly connectionRightHistoryTableConfig: TableConfig<ConnectionRight> = {
    ...connectionRightHistoryTable,
    pagination: true,
    emptyMessage: 'connectionRight.historyEmpty',
    refreshable: true,
    refresh: () => this.fetchConnectionRights(),
  };
  public dataColumns = [
    {title: 'connectionpoint.history.index', key: 'index'},
    {title: 'connectionpoint.identification', key: 'identification'},
    {title: 'connectionpoint.alternativeIdentification', key: 'alternativeIdentification'},
    {title: 'connectionpoint.stationsNumber', key: 'stationNumber'},
    {title: 'connectionpoint.sharedInstallation.hasSharedServiceLine', key: 'hasSharedServiceLine',
      cellRenderer: TranslateCellComponent, cellRendererParams: {true: 'common.yes', false: 'common.no'}},
    {title: 'connectionpoint.sharedInstallation.hasCollectiveMetering', key: 'hasCollectiveMetering',
      cellRenderer: TranslateCellComponent, cellRendererParams: {true: 'common.yes', false: 'common.no'}},
    {title: 'connectionpoint.sharedInstallation.numberOfApartments', key: 'numberOfApartments'},
    statePartial,
    ...systemServicesDetailsMasterdata,
    {title: 'address.streetCode', key: 'streetCode'},
    {title: 'address.streetName', key: 'streetName'},
    {title: 'address.streetNumber', key: 'buildingNumber'},
    {title: 'address.floor', key: 'floorIdentification'},
    {title: 'address.roomIdentification', key: 'roomIdentification'},
    {title: 'address.city', key: 'cityName'},
    {title: 'address.zipCode', key: 'postalCode'},
    {title: 'address.municipalcode', key: 'municipalityCode'},
    {title: 'address.countrycode', key: 'country'},
    {title: 'address.daraddress', key: 'darReference'},
  ];


  constructor(
    private readonly connectionPointService: ConnectionPointService,
    private readonly route: ActivatedRoute,
    private readonly stateManagerService: StateManagerService,
  ) {
    this.subscriptions.add(toObservable(this.stateManagerService.connectionPointState).subscribe(connectionPoint => {
      if (connectionPoint && connectionPoint !== this.connectionPoint &&
        (connectionPoint.id === this.id || connectionPoint.identification === this.id)) {
        this.updateConnectionPoint(connectionPoint);
      }
    }));
  }

  getValueByKey(obj: any, key: string): any {
    return key.split('.').reduce((acc, part) => acc?.[part], obj);
  }

  renderValue(row: any, col: any): string {
    return this.getValueByKey(row, col.key);
  }

  createRendererInjector(row: any, col: any): Injector {
    return Injector.create({
      providers: [
        {provide: 'value', useValue: this.getValueByKey(row, col.key)},
        {provide: 'row', useValue: row},
        {provide: 'column', useValue: col},
        {
          provide: 'params', useValue: {
            key: col.key,
            cellRendererParams: col.cellRendererParams
          }
        }
      ],
      parent: this.injector
    });
  }


  public ngOnInit(): void {
    this.subscriptions.add(this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.routeRequests.unsubscribe();
      this.historyRequest?.unsubscribe();
      this.rightsRequest?.unsubscribe();
      this.routeRequests = new Subscription();
      this.id = q.get('id')!;
      this.tableData = [];
      this.selectedElements = [];
      this.connectionPoint = undefined;
      this.connectionRights = [];
      this.fetchHistory();
      this.fetchConnectionPoint();
    }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.routeRequests.unsubscribe();
    this.historyRequest?.unsubscribe();
    this.rightsRequest?.unsubscribe();
  }

  public fetchHistory(): void {
    if (!this.id) return;
    this.historyRequest?.unsubscribe();
    this.historyState.set('loading');
    this.historyRequest = this.connectionPointService.connectionPointGethistoricalGet(this.id).pipe(
      finalize(() => {
        if (this.historyState() === 'loading') this.historyState.set('error');
      }),
    ).subscribe({
      next: data => {
        const sortedData = (data ?? []).sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        this.tableData = this.flattenData(sortedData);
        this.historyState.set('ready');
      },
      error: () => this.historyState.set('error'),
    });
  }

  public fetchConnectionPoint(): void {
    this.connectionPointState.set('loading');
    this.routeRequests.add(this.stateManagerService.getConnectionPoint(this.id).pipe(
      finalize(() => {
        if (this.connectionPointState() === 'loading') this.connectionPointState.set('error');
      }),
    ).subscribe({
      next: connectionPoint => this.updateConnectionPoint(connectionPoint),
      error: () => this.connectionPointState.set('error'),
    }));
  }

  public fetchConnectionRights(): void {
    if (!this.connectionPoint?.id || !this.connectionPoint.connectionPointOwnsConnectionRights) return;
    this.rightsRequest?.unsubscribe();
    this.rightsState.set('loading');
    this.rightsRequest = this.connectionPointService
      .connectionPointConnectionPointIdConnectionRightsGet(this.connectionPoint.id).pipe(
        finalize(() => {
          if (this.rightsState() === 'loading') this.rightsState.set('error');
        }),
      ).subscribe({
        next: rights => {
          this.connectionRights = [...rights];
          this.rightsState.set('ready');
        },
        error: () => this.rightsState.set('error'),
      });
  }

  private updateConnectionPoint(connectionPoint: ConnectionPointDto): void {
    if (connectionPoint.id !== this.id && connectionPoint.identification !== this.id) {
      this.connectionPointState.set('error');
      return;
    }
    const changed = this.connectionPoint !== connectionPoint;
    this.connectionPoint = connectionPoint;
    this.connectionPointState.set('ready');
    if (!changed) return;
    this.rightsRequest?.unsubscribe();
    this.connectionRights = [];
    if (connectionPoint.connectionPointOwnsConnectionRights) this.fetchConnectionRights();
  }


  private flattenData(data: ConnectionPointHistoricalDto[]): FlattenedConnectionPointHistoricalDto[] {
    return data.map(item => {
      return {
        index: item.index,
        identification: item.identification,
        alternativeIdentification: item.alternativeIdentification,
        stationNumber: item.stationNumber,
        hasSharedServiceLine: item.hasSharedServiceLine,
        hasCollectiveMetering: item.hasCollectiveMetering,
        numberOfApartments: item.numberOfApartments ?? null,
        state: item.state,
        systemServicesType: item.systemServicesType,
        systemServicesCapacity: item.systemServicesCapacity,
        date: item.date,
        action: item.action,
        changeBy: item.changeBy,

        streetCode: item.address?.streetCode,
        streetName: item.address?.streetName,
        buildingNumber: item.address?.buildingNumber,
        floorIdentification: item.address?.floorIdentification,
        roomIdentification: item.address?.roomIdentification,
        cityName: item.address?.cityName,
        postalCode: item.address?.postalCode,
        municipalityCode: item.address?.municipalityCode,
        country: item.address?.country,
        darReference: item.address?.darReference
      };
    });
  }

}
